import {
  ProgressBarWidgetConfig,
  ProgressBarWidgetDto,
  ResolvedProgressBarDeviceConfig,
} from "@intelligems/ig-types";
import { merge } from "@intelligems/ig-utils";
import { createProgressBarConfigForOffer } from "@intelligems/ig-utils/widgets/createProgressBarConfigForOffer";
import { createProgressBarConfigForSitewide } from "@intelligems/ig-utils/widgets/createProgressBarConfigForSitewide";
import {
  resolveProgressBarDesktopConfig,
  resolveProgressBarMobileConfig,
  resolveProgressBarWidgetConfig,
} from "@intelligems/ig-widgets/ProgressBarView/resolveProgressBarWidgetConfig";
import { createSelector } from "@reduxjs/toolkit";
import { offerExample } from "@src/domains/WidgetLibrary/stubs";
import { RootState, useSelector, useSelectorFactory } from "@src/store";
import { getVariationOffer } from "@src/store/slices/offers/selectors";
import { getProgressBarInterfaceForVariation } from "@src/store/slices/userInterfaces/selectors";
import { getProgressBar, selectedWidgets } from "@src/store/slices/widgets";
import { ProgressBarConfig } from "@src/store/slices/widgets/types";
import { filterWidgets } from "@src/store/slices/widgets/util";

export const getLibraryProgressBars = createSelector(
  selectedWidgets,
  (widgets) =>
    filterWidgets<ProgressBarWidgetDto>(widgets, {
      discountProgressBar: true,
      shippingProgressBar: true,
    })
);

export const getLibraryProgressBar =
  (id?: string, widgetOverride?: ProgressBarWidgetDto) =>
  (state: RootState) => {
    if (id === undefined) {
      return getProgressBar(state);
    } else {
      const progressBars = getLibraryProgressBars(state);
      return progressBars.find((w) => w.id === id);
    }
  };

export const getLibraryOfferProgressBars = createSelector(
  selectedWidgets,
  (widgets) =>
    filterWidgets<ProgressBarWidgetDto>(widgets, { discountProgressBar: true })
);

export const getLibraryShippingProgressBars = createSelector(
  selectedWidgets,
  (widgets) =>
    filterWidgets<ProgressBarWidgetDto>(widgets, { shippingProgressBar: true })
);

// combines interface widget with user interface variables
export const getUserInterfaceProgressBar = (variationId: string) =>
  createSelector(
    getProgressBarInterfaceForVariation(variationId),
    (userInterface) => {
      if (!userInterface?.widget) return undefined;

      const desktopStyles = userInterface.styleOverrides
        ? merge(
            userInterface.widget.config?.desktop?.styles || {},
            userInterface.styleOverrides
          )
        : userInterface.widget.config?.desktop?.styles;

      const mobileStyles = userInterface.styleOverrides
        ? merge(
            userInterface.widget.config?.mobile?.styles || {},
            userInterface.styleOverrides
          )
        : userInterface.widget.config?.mobile?.styles;

      return merge(userInterface.widget, {
        config: {
          desktop: {
            variables: userInterface.desktopVariables,
            styles: desktopStyles,
          },
          mobile: {
            variables: userInterface.mobileVariables,
            styles: mobileStyles,
          },
        },
      });
    }
  );
export const useUserInterfaceProgressBar = (variationId: string) =>
  useSelectorFactory(getUserInterfaceProgressBar, variationId);

export const getLibraryProgressBarConfig = (
  widgetId?: string,
  widgetOverride?: ProgressBarWidgetDto
) =>
  createSelector(getLibraryProgressBar(widgetId, widgetOverride), (widget) => {
    if (!widget) return undefined;

    if (widget.widgetType === "shippingProgressBar") {
      const widgetConfig = resolveProgressBarWidgetConfig(
        widgetOverride || widget
      );

      return createProgressBarConfigForSitewide(widgetConfig);
    } else {
      const widgetConfig = resolveProgressBarWidgetConfig(
        widgetOverride || widget,
        offerExample
      );
      return createProgressBarConfigForOffer(offerExample, widgetConfig, true);
    }
  });
export const useLibraryProgressBarConfig = (
  widgetId?: string,
  widgetOverride?: ProgressBarWidgetDto
) => useSelectorFactory(getLibraryProgressBarConfig, widgetId, widgetOverride);

export const isDiscountProgressBarEnabled =
  (variationId: string) => (state: RootState) => {
    const offer = getVariationOffer(variationId)(state);
    const tiers = offer?.tiers ?? [];
    return tiers.some((t) => t.minimumUnits > 0);
  };

export const isShippingProgressBarEnabled =
  (variationId: string) => (state: RootState) => {
    const offer = getVariationOffer(variationId)(state);
    if (offer) {
      const tiers = offer.tiers ?? [];
      return tiers.some((t) => t.isFreeShipping);
    } else {
      return true;
    }
  };

export const getResolvedDesktopProgressBarConfig = createSelector(
  getProgressBar,
  (widget) => {
    if (widget === undefined) return undefined;
    return resolveProgressBarDesktopConfig(widget.config);
  }
);

export const getResolvedMobileProgressBarConfig = createSelector(
  getProgressBar,
  (widget) => {
    if (widget === undefined) return undefined;
    return resolveProgressBarMobileConfig(widget.config);
  }
);

export const getDesktopProgressBarConfig = createSelector(
  getProgressBar,
  getResolvedDesktopProgressBarConfig,
  (widget, config) => getProgressBarConfig(widget, config)
);

export const getMobileProgressBarConfig = createSelector(
  getProgressBar,
  getResolvedMobileProgressBarConfig,
  (widget, config) => getProgressBarConfig(widget, config)
);

export const areDeviceProgressBarDifferent = createSelector(
  getResolvedDesktopProgressBarConfig,
  getResolvedMobileProgressBarConfig,
  (desktop, mobile) =>
    JSON.stringify({
      variant: desktop?.variant,
      variables: desktop?.variables,
      styles: desktop?.styles,
    }) !==
    JSON.stringify({
      variant: mobile?.variant,
      variables: mobile?.variables,
      styles: mobile?.styles,
    })
);

function getProgressBarConfig(
  widget: ProgressBarWidgetDto | undefined,
  config: ResolvedProgressBarDeviceConfig | undefined
): ProgressBarConfig | undefined {
  if (widget === undefined || config === undefined) return undefined;

  const styles = config.styles;

  const template: ProgressBarConfig = {
    general: {
      name: {
        value: widget.name,
        path: "name",
      },
      variant: {
        value: config.variant ?? "progressBar1",
        path: "variant",
      },
      position: {
        value: styles.general.cartTextPosition,
        path: "styles.general.cartTextPosition",
      },
    },
    siteWide: getSiteWideConfig(widget, config),
    bar: {
      activeColor: {
        value: styles.bar.activeColor,
        path: "styles.bar.activeColor",
      },
      inactiveColor: {
        value: styles.bar.inactiveColor,
        path: "styles.bar.inactiveColor",
      },
      thickness: {
        value: styles.bar.thickness,
        path: "styles.bar.thickness",
      },
      cornerRadius: {
        value: styles.bar.cornerRadius,
        path: "styles.bar.cornerRadius",
      },
      tooltipBackgroundColor: {
        value: styles.tooltip.backgroundColor,
        path: "styles.tooltip.backgroundColor",
      },
    },
    breakpoint: {
      activeColor: {
        value: styles.breakpoint.activeColor,
        path: "styles.breakpoint.activeColor",
      },
      inactiveColor: {
        value: styles.breakpoint.inactiveColor,
        path: "styles.breakpoint.inactiveColor",
      },
      activeIconColor: {
        value: styles.breakpoint.activeIconColor,
        path: "styles.breakpoint.activeIconColor",
      },
      inactiveIconColor: {
        value: styles.breakpoint.inactiveIconColor,
        path: "styles.breakpoint.inactiveIconColor",
      },
      size: {
        value: styles.breakpoint.size,
        path: "styles.breakpoint.size",
      },
      hideText: {
        value: styles.breakpoint.hideText,
        path: "styles.breakpoint.hideText",
      },
    },
  };

  switch (config.variant) {
    case "progressBar1":
    case "progressBar2":
    default:
      return {
        general: template.general,
        siteWide: template.siteWide,
        bar: {
          activeColor: template.bar?.activeColor,
          inactiveColor: template.bar?.inactiveColor,
          thickness: template.bar?.thickness,
          cornerRadius: template.bar?.cornerRadius,
        },
        breakpoint: template.breakpoint,
      };
    case "progressBar3":
      return {
        general: template.general,
        siteWide: template.siteWide,
        bar: {
          activeColor: template.bar?.activeColor,
          inactiveColor: template.bar?.inactiveColor,
          thickness: template.bar?.thickness,
          cornerRadius: template.bar?.cornerRadius,
        },
      };
    case "progressBarWidget1":
      return {
        general: {
          name: template.general?.name,
          variant: template.general?.variant,
        },
        siteWide: template.siteWide,
        bar: {
          activeColor: template.bar?.activeColor,
          inactiveColor: template.bar?.inactiveColor,
          tooltipBackgroundColor: template.bar?.tooltipBackgroundColor,
        },
      };
    case "progressBarWidget2":
    case "progressBarWidget4":
      return {
        general: {
          name: template.general?.name,
          variant: template.general?.variant,
        },
        siteWide: template.siteWide,
        bar: {
          activeColor: template.bar?.activeColor,
          inactiveColor: template.bar?.inactiveColor,
          tooltipBackgroundColor: template.bar?.tooltipBackgroundColor,
        },
        breakpoint: {
          activeColor: template.breakpoint?.activeColor,
          inactiveColor: template.breakpoint?.inactiveColor,
        },
      };
  }
}

function getSiteWideConfig(
  widget: ProgressBarWidgetDto,
  config: ResolvedProgressBarDeviceConfig
) {
  if (widget.widgetType === "shippingProgressBar") {
    const variables = config.variables;
    return {
      threshold: {
        value: variables.defaultShippingThreshold,
        path: "variables.defaultShippingThreshold",
      },
      aboveThreshold: {
        value: variables.textReplacements.currentUnlockedTemplate,
        path: "variables.textReplacements.currentUnlockedTemplate",
      },
      aboveThresholdType: {
        value: variables.textReplacements.currentUnlockedTemplateType,
        path: "variables.textReplacements.currentUnlockedTemplateType",
      },
      underThreshold: {
        value: variables.textReplacements.buyMoreTemplate,
        path: "variables.textReplacements.buyMoreTemplate",
      },
      underThresholdType: {
        value: variables.textReplacements.buyMoreTemplateType,
        path: "variables.textReplacements.buyMoreTemplateType",
      },
    } as const;
  }
}
